import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/** Live DB: CityId 1 = Rajkot */
export const DEFAULT_CITY_ID = 1
export const DEFAULT_CITY_NAME = 'Rajkot'

interface CityState {
  cityId: number | null
  cityName: string
  /** True once the user has explicitly picked a city — auto-detect won't override it. */
  manual: boolean
  /** True once geolocation has resolved a city at least once. */
  detected: boolean
  setCity: (id: number, name: string) => void
  /** Apply a geo-detected city only if the user hasn't chosen one manually. */
  setDetectedCity: (id: number, name: string) => void
  /** Explicit "Use my location" action should override previous manual city. */
  setLocationCity: (id: number, name: string) => void
  /** Fallback when location is denied or detection fails (skipped if manual). */
  setDefaultCity: () => void
  updateCityName: (name: string) => void
}

export const useCityStore = create<CityState>()(
  persist(
    (set, get) => ({
      // CityId 1 = Rajkot in the live DB. Geolocation overrides this on first
      // visit (unless the user has manually picked a city).
      cityId: DEFAULT_CITY_ID,
      cityName: DEFAULT_CITY_NAME,
      manual: false,
      detected: false,
      setCity: (cityId, cityName) => set({ cityId, cityName, manual: true }),
      setDetectedCity: (cityId, cityName) => {
        if (get().manual) {
          set({ detected: true })
          return
        }
        set({ cityId, cityName, detected: true })
      },
      setLocationCity: (cityId, cityName) => set({ cityId, cityName, detected: true, manual: true }),
      setDefaultCity: () => {
        if (get().manual) return
        set({ cityId: DEFAULT_CITY_ID, cityName: DEFAULT_CITY_NAME, detected: true })
      },
      updateCityName: (cityName) => set({ cityName }),
    }),
    {
      name: 'qbc-city',
      version: 2,
      migrate: (persisted: unknown, version: number) => {
        const state = (persisted ?? {}) as Partial<CityState>
        // v1 hardcoded an incorrect "Ahmedabad" label on CityId 1 (= Rajkot).
        // Reset auto/default selections so detection picks the real city.
        if (version < 2 && !state.manual) {
          return { ...state, cityId: 1, cityName: 'Rajkot', detected: false }
        }
        return state as CityState
      },
    },
  ),
)
